import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  MailOutline,
  PersonAddAlt1,
  Send as SendIcon,
  HourglassEmpty as HourglassIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  getOrganizationUsers,
  inviteUsers,
  getInvitedUsers,
  getRoles,
  getCurrentUser,
  deleteInvite,
} from '../services/api.js';
import { getCurrentUser as getStoredUser } from '../utils/auth.js';

// getStoredUser is in-memory UX cache only; authorization is enforced by the API.

const STATUS_COLORS = {
  used: { bg: '#E8F5E9', color: '#2E7D32', label: 'Utilizado' },
  pending: { bg: '#FFF8E1', color: '#F57F17', label: 'Pendente' },
  expired: { bg: '#FFEBEE', color: '#C62828', label: 'Expirado' },
};

const getInviteStatus = invite => {
  if (invite.dt_utilizado) return STATUS_COLORS.used;
  if (invite.dt_limite && new Date(invite.dt_limite) < new Date())
    return STATUS_COLORS.expired;
  return STATUS_COLORS.pending;
};

const formatDate = value => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('pt-BR');
  } catch {
    return value;
  }
};

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [emails, setEmails] = useState(['']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersData, rolesData, userData, invitedUsersData] =
          await Promise.all([
            getOrganizationUsers(),
            getRoles().catch(() => []),
            getCurrentUser().catch(() => null),
            getInvitedUsers().catch(() => []),
          ]);
        setUsers(usersData);
        setRoles(rolesData);
        setInvitedUsers(invitedUsersData);
        setCurrentUser(userData || getStoredUser());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const isMonitor =
    currentUser &&
    roles.length > 0 &&
    (() => {
      const userRole = roles.find(r => r.id_role === currentUser.id_role);
      return userRole && userRole.cd_role === 'MON';
    })();

  const getRoleName = roleId => {
    const role = roles.find(r => r.id_role === roleId);
    return role ? role.nm_role : `Perfil ${roleId}`;
  };

  // Set of already-invited emails (lowercase) for duplicate checking
  const alreadyInvitedEmails = new Set(
    invitedUsers.map(inv => (inv.nm_email_usuario_invite || '').toLowerCase())
  );

  const handleOpenDialog = () => {
    setEmails(['']);
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEmails(['']);
    setError('');
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const handleAddEmail = () => {
    setEmails([...emails, '']);
  };

  const handleRemoveEmail = index => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails.length > 0 ? newEmails : ['']);
  };

  const getEmailFieldError = email => {
    if (email.trim() === '') return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return 'E-mail inválido';
    if (alreadyInvitedEmails.has(email.trim().toLowerCase()))
      return 'Este e-mail já foi convidado';
    return null;
  };

  const handleInviteUsers = async () => {
    const emailList = emails.map(e => e.trim()).filter(e => e !== '');

    if (emailList.length === 0) {
      setError('Por favor, insira pelo menos um e-mail.');
      return;
    }

    // Check for already-invited emails
    const alreadyInvited = emailList.filter(e =>
      alreadyInvitedEmails.has(e.toLowerCase())
    );
    if (alreadyInvited.length > 0) {
      setError(
        `Os seguintes e-mails já foram convidados: ${alreadyInvited.join(', ')}`
      );
      return;
    }

    // Validate format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalid = emailList.filter(e => !emailRegex.test(e));
    if (invalid.length > 0) {
      setError(`E-mails inválidos: ${invalid.join(', ')}`);
      return;
    }

    try {
      setSubmitting(true);
      const user = currentUser || getStoredUser();
      const institutionId = user?.id_instituicao;
      if (!institutionId) {
        setError(
          'Você precisa estar vinculado a uma instituição para convidar usuários.'
        );
        return;
      }
      await inviteUsers(institutionId, emailList);
      setSuccess(
        `${emailList.length === 1 ? 'Convite enviado' : `${emailList.length} convites enviados`} com sucesso!`
      );
      handleCloseDialog();
      const [data, invitedData] = await Promise.all([
        getOrganizationUsers(),
        getInvitedUsers(),
      ]);
      setUsers(data);
      setInvitedUsers(invitedData);
    } catch (err) {
      if (err.emails_bloqueados && err.emails_bloqueados.length > 0) {
        const errorMsg = err.emails_bloqueados
          .map(item => `${item.email} (${item.motivo})`)
          .join(', ');
        setError(`E-mail(s) com restrição: ${errorMsg}`);
      } else {
        setError(err.message || 'Erro ao enviar convites.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteInvite = async inviteId => {
    if (!inviteId) return;
    if (!window.confirm('Tem certeza de que deseja deletar este convite?'))
      return;
    try {
      setLoading(true);
      await deleteInvite(inviteId);
      setSuccess('Convite deletado com sucesso!');
      const invitedData = await getInvitedUsers().catch(() => []);
      setInvitedUsers(invitedData);
    } catch (err) {
      setError(err.message || 'Erro ao deletar convite.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  if (loading && users.length === 0) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='60vh'
      >
        <CircularProgress sx={{ color: '#88C9AE' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#F8F9FA',
        minHeight: '100vh',
        p: { xs: '16px', sm: '24px 32px', md: '40px 60px' },
        boxSizing: 'border-box',
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 5,
        }}
      >
        <Box>
          <Typography
            component='h1'
            sx={{
              fontWeight: 900,
              fontSize: { xs: '24px', md: '32px' },
              color: '#1A2340',
              lineHeight: 1.2,
            }}
          >
            Gerenciamento de Usuários
          </Typography>
          <Typography
            sx={{ color: '#999', fontSize: '14px', mt: 0.5, fontWeight: 600 }}
          >
            Gerencie convites e membros da sua instituição
          </Typography>
        </Box>

        <Button
          variant='contained'
          startIcon={<PersonAddAlt1 />}
          onClick={handleOpenDialog}
          sx={{
            bgcolor: '#1A2340',
            color: '#FFF',
            '&:hover': { bgcolor: '#2C3E67' },
            fontWeight: 800,
            borderRadius: '14px',
            px: { xs: 3, sm: 4 },
            py: 1.5,
            textTransform: 'none',
            fontSize: '14px',
            boxShadow: '0 4px 16px rgba(26,35,64,0.18)',
            whiteSpace: 'nowrap',
          }}
        >
          Convidar Usuários
        </Button>
      </Box>

      {/* Stats Row */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          mb: 5,
        }}
      >
        {[
          {
            label: 'Convites Enviados',
            value: invitedUsers.length,
            color: '#1A2340',
            bgLight: '#E8EAF0',
            icon: <SendIcon sx={{ fontSize: 24, color: '#1A2340' }} />,
          },
          {
            label: 'Convites Pendentes',
            value: invitedUsers.filter(i => !i.dt_utilizado).length,
            color: '#F57F17',
            bgLight: '#FFF8E1',
            icon: <HourglassIcon sx={{ fontSize: 24, color: '#F57F17' }} />,
          },
          {
            label: 'Convites Utilizados',
            value: invitedUsers.filter(i => i.dt_utilizado).length,
            color: '#2E7D32',
            bgLight: '#E8F5E9',
            icon: <CheckCircleIcon sx={{ fontSize: 24, color: '#2E7D32' }} />,
          },
        ].map(stat => (
          <Paper
            key={stat.label}
            sx={{
              p: '24px 28px',
              borderRadius: '20px',
              flex: 1,
              minWidth: { xs: 'calc(50% - 12px)', sm: '200px' },
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
              bgcolor: '#FFF',
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
              },
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '16px',
                bgcolor: stat.bgLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {stat.icon}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: '32px',
                  color: stat.color,
                  lineHeight: 1,
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 700,
                  color: '#999',
                  textTransform: 'uppercase',
                  mt: 0.5,
                  letterSpacing: '0.5px',
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Invites Table */}
      <Paper
        sx={{
          borderRadius: '20px',
          boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: '20px 24px',
            borderBottom: '1px solid #F0F0F0',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <MailOutline sx={{ color: '#88C9AE', fontSize: 20 }} />
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '13px',
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              color: '#1A2340',
            }}
          >
            Convites Enviados
          </Typography>
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 460 }}>
            <TableHead sx={{ bgcolor: '#F8F9FA' }}>
              <TableRow>
                {['E-mail', 'Data Limite', 'Status', 'Ações'].map(col => (
                  <TableCell
                    key={col}
                    align={col === 'Ações' ? 'right' : 'left'}
                    sx={{
                      fontWeight: 900,
                      fontSize: '12px',
                      color: '#1A2340',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      py: 2,
                      borderBottom: '2px solid #F0F0F0',
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {invitedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align='center' sx={{ py: 8 }}>
                    <Typography
                      sx={{ color: '#CCC', fontSize: '14px', fontWeight: 700 }}
                    >
                      Nenhum convite enviado ainda
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invitedUsers.map((invite, idx) => {
                  const status = getInviteStatus(invite);
                  return (
                    <TableRow
                      key={invite.id_instituicao_acesso || idx}
                      sx={{
                        '&:nth-of-type(odd)': { bgcolor: '#FFF' },
                        '&:nth-of-type(even)': { bgcolor: '#FAFAFA' },
                        '&:hover': { bgcolor: '#F1F3F9' },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell
                        sx={{
                          color: '#444',
                          py: 2.5,
                          fontWeight: 600,
                          fontSize: '14px',
                        }}
                      >
                        {invite.nm_email_usuario_invite}
                      </TableCell>
                      <TableCell
                        sx={{ color: '#666', py: 2.5, fontSize: '14px' }}
                      >
                        {formatDate(invite.dt_limite)}
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Box
                          component='span'
                          sx={{
                            display: 'inline-block',
                            px: 2,
                            py: 0.5,
                            borderRadius: '20px',
                            bgcolor: status.bg,
                            color: status.color,
                            fontWeight: 800,
                            fontSize: '12px',
                          }}
                        >
                          {status.label}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }} align='right'>
                        <IconButton
                          size='small'
                          color='error'
                          onClick={() =>
                            handleDeleteInvite(invite.id_instituicao_acesso)
                          }
                          disabled={!!invite.dt_utilizado}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Invite Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            color: '#1A2340',
            fontSize: '20px',
            pb: 1,
          }}
        >
          Convidar Usuários
        </DialogTitle>
        <DialogContent>
          <Typography
            variant='body2'
            sx={{ color: '#888', mb: 3, fontWeight: 600 }}
          >
            Insira os e-mails dos usuários que deseja convidar para sua
            instituição.
          </Typography>

          {error && (
            <Alert
              severity='error'
              sx={{ mb: 2, borderRadius: '12px' }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}
          >
            {emails.map((email, idx) => {
              const fieldError = getEmailFieldError(email);
              return (
                <Box
                  key={idx}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                >
                  <TextField
                    autoFocus={idx === emails.length - 1 && idx > 0}
                    id={`email-${idx}`}
                    label={`E-mail ${idx + 1}`}
                    type='email'
                    fullWidth
                    variant='outlined'
                    value={email}
                    onChange={e => handleEmailChange(idx, e.target.value)}
                    placeholder='exemplo@dominio.com'
                    error={!!fieldError}
                    helperText={fieldError || ''}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', mt: 1 }}>
                    {emails.length > 1 && (
                      <IconButton
                        color='error'
                        onClick={() => handleRemoveEmail(idx)}
                        title='Remover e-mail'
                        size='small'
                        sx={{ mt: 0.5 }}
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    )}
                    {idx === emails.length - 1 && (
                      <IconButton
                        onClick={handleAddEmail}
                        title='Adicionar outro e-mail'
                        size='small'
                        sx={{ mt: 0.5, color: '#1A2340' }}
                      >
                        <AddIcon fontSize='small' />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#666',
              fontWeight: 700,
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleInviteUsers}
            variant='contained'
            disabled={submitting}
            sx={{
              bgcolor: '#1A2340',
              color: '#FFF',
              '&:hover': { bgcolor: '#2C3E67' },
              fontWeight: 800,
              borderRadius: '12px',
              px: 4,
              textTransform: 'none',
              fontSize: '14px',
              boxShadow: '0 4px 12px rgba(26,35,64,0.2)',
            }}
          >
            {submitting ? (
              <CircularProgress size={22} color='inherit' />
            ) : (
              'Enviar Convites'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity='success'
          sx={{ width: '100%', borderRadius: '12px', fontWeight: 700 }}
        >
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error && !openDialog}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity='error'
          sx={{ width: '100%', borderRadius: '12px', fontWeight: 700 }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagementPage;
